import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Loader2 } from "lucide-react";

// Conferma + esito dell'eliminazione di massa (unica chiamata server-side)
export function EliminaMassaArticoli({ n, payload, onClose, onDone }) {
  const [testo, setTesto] = useState("");
  const [esito, setEsito] = useState(null);
  const serveParola = n > 100;
  const mut = useMutation({
    mutationFn: async () => (await api.post("/magazzino/articoli/elimina-massa", payload, { timeout: 600000 })).data,
    onSuccess: (d) => { setEsito(d); onDone(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore durante l'eliminazione"),
  });
  return (
    <Dialog open onOpenChange={(v) => !v && !mut.isPending && onClose()}>
      <DialogContent className="max-w-xl" data-testid="articoli-elimina-massa-dialog">
        {!esito ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-red-700">Elimina {n} articoli</DialogTitle>
              <DialogDescription>Stai per eliminare {n} articoli. L'operazione non è reversibile. Confermi? Gli articoli collegati a movimenti di magazzino, commesse o altri documenti non verranno eliminati.</DialogDescription>
            </DialogHeader>
            {serveParola && (
              <div className="space-y-1.5 text-sm">
                <div>Per confermare scrivi <b className="font-mono">ELIMINA</b></div>
                <Input value={testo} onChange={(e) => setTesto(e.target.value.toUpperCase())} placeholder="ELIMINA" className="font-mono" autoFocus data-testid="articoli-elimina-massa-parola" />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={mut.isPending} data-testid="articoli-elimina-massa-annulla">Annulla</Button>
              <Button variant="destructive" disabled={mut.isPending || (serveParola && testo !== "ELIMINA")} onClick={() => mut.mutate()} data-testid="articoli-elimina-massa-conferma">
                {mut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />} {mut.isPending ? "Eliminazione in corso…" : `Elimina ${n} articoli`}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Eliminati {esito.eliminati}, non eliminati {esito.non_eliminati}</DialogTitle>
              <DialogDescription data-testid="articoli-elimina-massa-esito">{esito.non_eliminati > 0 ? "Gli articoli seguenti non sono stati eliminati perché collegati a documenti:" : "Tutti gli articoli selezionati sono stati eliminati."}</DialogDescription>
            </DialogHeader>
            {esito.bloccati.length > 0 && (
              <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200 text-sm" data-testid="articoli-elimina-massa-bloccati">
                {esito.bloccati.map((b) => (
                  <div key={b.codice} className="px-3 py-1.5 border-b border-slate-100 last:border-0 flex gap-2"><span className="font-mono text-xs w-28 shrink-0">{b.codice}</span><span className="truncate flex-1">{b.descrizione}</span><span className="text-amber-700 text-xs shrink-0">{b.motivo}</span></div>
                ))}
              </div>
            )}
            <DialogFooter><Button onClick={onClose} data-testid="articoli-elimina-massa-chiudi">Chiudi</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
